// src/helpers/checkPermission.js
import {
  getSavedModuleDefaultPath,
  persistModuleDefaultPath,
} from "./moduleDefaultPath";
import { getPimsDefaultPath } from "../pages/Pims/pimsDefaultPath";
import {
  QUESTIONNAIRE_PERMISSION_KEYS,
  resolveQuestionnairePermissionKey,
} from "../constants/questionnairePermissions";

/**
 * 設定 → 病歷管理：後端 functions 的第三層是頁面名稱（罐頭訊息）而非「查看」，
 * 做法比照「隱碼設定」。因此其子頁不可用 checkView，需以 methodKey 指定方法鍵名。
 */
const MEDICAL_RECORD_PERMISSION_KEY = "病歷管理";
const MEDICAL_RECORD_METHOD_KEYS = {
  cannedMessage: "罐頭訊息",
  treatmentRecordTemplates: "療程紀錄模板",
};

/**
 * 設定側欄項目標題 → 權限鍵名。
 * 側欄葉節點標題（罐頭訊息）與權限第二層（病歷管理）不同名時必須列在此，
 * 否則 DropDownOption 會拿標題直接查 permissions.設定.罐頭訊息 而找不到，判定為權限不足。
 */
const OPTIONS_MENU_PERMISSION_BY_TITLE = {
  罐頭訊息: {
    subKey: MEDICAL_RECORD_PERMISSION_KEY,
    methodKey: MEDICAL_RECORD_METHOD_KEYS.cannedMessage,
  },
  療程紀錄模板: {
    subKey: MEDICAL_RECORD_PERMISSION_KEY,
    methodKey: MEDICAL_RECORD_METHOD_KEYS.treatmentRecordTemplates,
  },
};

/**
 * 取得設定側欄項目對應的權限鍵；無特殊對應時回傳 null（由呼叫端沿用標題）。
 * @returns {{ subKey: string, methodKey?: string } | null}
 */
function resolveOptionsMenuPermission(title) {
  return OPTIONS_MENU_PERMISSION_BY_TITLE[title] || null;
}

/**
 * URL 路徑段 → 母類別權限鍵名
 */
const CATEGORY_MAP = {
  statistics: "數據",
  sales: "銷售",
  customer: "會員",
  appointment: "預約",
  tickets: "課程",
  shiftschedule: "班表",
  options: "設定",
  accounting: "帳務",
};

/**
 * URL 子路徑 → 子頁面權限對應表
 * key 格式: "母路徑/子路徑" 或 "母路徑"（母路徑即子頁面，如預約）
 * value: { subKey: API 子權限鍵名 }
 *
 * 若 URL 不在此表中，僅檢查母類別權限（如 /sales、/accounting 母頁）
 * 庫存（PIMS）有獨立的 checkPimsPermission()，不在此表中
 */
/**
 * 僅 UI 隱藏或未啟用之功能，仍可能存在路由；直連時一律擋下（與後端權限無關）。
 * key 格式與 SUB_PAGE_PERMISSIONS 相同：「母路徑/子路徑」
 *
 * 對應邏輯見 {@link isAllowed}（子路徑在此集合時一律不允許）。
 * 各模組 Layout 的權限 useEffect 請**先**判斷 {@link isClosedFeatureRoute} 並顯示
 * 「本功能未開放」，**再**做 {@link isAllowed}；否則關閉中的頁面會被當成一般無權限而誤導向其他子頁。
 *
 * 單段路徑（如 /test、/overview）見 {@link CLOSED_FEATURE_ROOT_SEGMENTS}，由 {@link isClosedFeatureRoute} 一併判斷。
 */
const CLOSED_FEATURE_SUBPATHS = new Set([
  "sales/topup",
  "sales/redemption",
  "accounting/outstanding-details",
  // 修改密碼目前移至設定並開放所有帳號使用
  "statistics/commentAnalysis",
  "test/print-receipt",
  // 正式版暫關：卡項管理（側欄另註解；直連顯示本功能未開放）
  "tickets/cardManagement",
]);

/**
 * 僅一段 path 時仍應擋下的「未開放」路由（如 /overview、/test）。
 */
const CLOSED_FEATURE_ROOT_SEGMENTS = new Set(["test", "overview"]);

/**
 * 是否為「本功能未開放」之直連路由（與權限物件無關，依 URL 與
 * {@link CLOSED_FEATURE_SUBPATHS}、{@link CLOSED_FEATURE_ROOT_SEGMENTS}）
 */
function isClosedFeatureRoute(url) {
  if (!url || typeof url !== "string") return false;
  const parts = url.split("/").filter(Boolean);
  if (parts.length === 1 && CLOSED_FEATURE_ROOT_SEGMENTS.has(parts[0])) {
    return true;
  }
  if (parts.length < 2) return false;
  return CLOSED_FEATURE_SUBPATHS.has(`${parts[0]}/${parts[1]}`);
}

/**
 * 班表同意書子路由 eng → Redux「班表」子權限鍵名
 * - 範本設定／設定服務分類／服務關聯設定：術前同意書
 * - 付款同意書：付款三子頁共用「付款同意書・查看」
 */
const SHIFT_SCHEDULE_CONSENT_PERMISSION_BY_ENG = {
  "consent-templates": "範本設定",
  "consent-editor": "範本設定",
  "consent-category-link": "設定服務分類",
  "consent-category-edit": "設定服務分類",
  "consent-service-link": "服務關聯設定",
  "consent-service-edit": "服務關聯設定",
  "payment-consent-template": "付款同意書",
  "payment-consent-category-link": "付款同意書",
  "payment-consent-category-edit": "付款同意書",
  "payment-consent-service-link": "付款同意書",
  "payment-consent-service-edit": "付款同意書",
  "post-treatment-consent-template": "療程後確認單",
};

/** 班表同意書明細／編輯子路由 → 側欄對應列表頁 eng（維持母層選單展開與高亮） */
const SHIFT_SCHEDULE_MENU_PAGINATION_ALIASES = {
  "consent-editor": "consent-templates",
  "consent-category-edit": "consent-category-link",
  "consent-service-edit": "consent-service-link",
  "payment-consent-category-edit": "payment-consent-category-link",
  "payment-consent-service-edit": "payment-consent-service-link",
  "questionnaire-editor": "questionnaire-templates",
  "questionnaire-category-edit": "questionnaire-category-link",
  "questionnaire-service-edit": "questionnaire-service-link",
};

function resolveShiftSchedulePermissionSubKey(eng, fallbackTitle) {
  return (
    resolveQuestionnairePermissionKey(eng) ||
    SHIFT_SCHEDULE_CONSENT_PERMISSION_BY_ENG[eng] ||
    fallbackTitle
  );
}

function resolveShiftScheduleMenuPagination(pagination) {
  if (!pagination || typeof pagination !== "string") return pagination;
  return SHIFT_SCHEDULE_MENU_PAGINATION_ALIASES[pagination] || pagination;
}

const SUB_PAGE_PERMISSIONS = {
  // ===== 設定 =====
  "options/company": { subKey: "公司資訊" },
  "options/branch": { subKey: "店家資訊" },
  "options/label": { subKey: "標籤管理" },
  // 須具備「查看」才可進入路由（與後端 Levels GET 權限一致）
  "options/member-management": { subKey: "等級設定", checkView: true },
  "options/purchase-gift": { subKey: "贈品規則", checkView: true }, // 查看 154；新增 155、編輯 156、刪除 157 由頁面控管
  "options/intake-form": { subKey: "初診單設定", checkView: true },
  // 病歷管理底下各頁以自己的 ch_method 控管（非「查看」）
  "options/canned-message": {
    subKey: MEDICAL_RECORD_PERMISSION_KEY,
    methodKey: MEDICAL_RECORD_METHOD_KEYS.cannedMessage,
  },
  "options/treatment-record-templates": {
    subKey: MEDICAL_RECORD_PERMISSION_KEY,
    methodKey: MEDICAL_RECORD_METHOD_KEYS.treatmentRecordTemplates,
  },
  "options/autoreply": { subKey: "自動化訊息設定" },
  "options/accountauthority": { subKey: "帳號權限管理" },
  "options/payment-gateway": { subKey: "API串接", checkView: true },
  "options/preferences": { subKey: "喜好設定", alwaysAllow: true },
  "options/password": { subKey: "修改密碼", alwaysAllow: true },
  "options/pay": { subKey: "POS付款/發票設定", checkView: true }, // 查看 81（dev 既有）
  "options/consulting": { subKey: "診間設定", checkView: true }, // 查看 136（dev 既有；migration 語意去重）
  // "options/serviceplan": { subKey: "服務方式及繳款" },
  // "options/setPaymentMethod": { subKey: "設定付款方式" },
  // "options/point": { subKey: "品牌點數管理" },
  // "options/topup": { subKey: "儲值金管理" },
  // "options/messagetemplate": { subKey: "訊息範本管理" },
  // "options/level": { subKey: "等級設定" },
  // "options/blacklist": { subKey: "黑名單管理" }, // API 有，無對應路由
  // "options/cancellationpolicy": { subKey: "取消政策" }, // API 有，無對應路由
  // "options/deposit": { subKey: "訂金管理" }, // API 有，無對應路由

  // ===== 銷售 =====
  "sales/ticket": { subKey: "課程結帳" },
  // "sales/topup": { subKey: "儲值金結帳" },
  "sales/appointment-checkoff": { subKey: "結帳紀錄", checkView: true },
  "sales/checkoutrecord": { subKey: "結帳紀錄", checkView: true },
  "sales/dashboard": { subKey: "診所營運戰情室", checkView: true }, // 查看 187
  // 耗材紀錄無獨立子權限，僅檢查「銷售」母類別（同 balance-payment）
  // 銷售首頁：/sales、/sales/index 刻意不列入此表 → isAllowed 僅檢查 permissions「銷售」.enable，兩網址相同
  // 其餘無映射子路徑（如 balance-payment、redemption）→ 亦僅母類別

  // ===== 帳務 =====
  "accounting/close": { subKey: "櫃檯人員日結", checkView: true },
  "accounting/close-record": { subKey: "日結記錄", checkView: true },
  "accounting/cash-withdraw": { subKey: "現金提領", checkView: true },
  "accounting/account-audit": { subKey: "帳務審核", checkView: true },
  // "accounting/outstanding-details": { subKey: "欠款明細" },

  // ===== 數據 =====
  "statistics/performanceReport": { subKey: "業績報表" },
  "statistics/virtualPerformanceReport": { subKey: "業績報表" }, // 虛擬業績報表無獨立權限，沿用業績報表
  "statistics/consultantPerformance": { subKey: "諮詢師業績", altSubKeys: ["美容顧問業績"] },
  "statistics/orderPerformanceDetails": { subKey: "訂單業績明細" },
  "statistics/verificationReport": { subKey: "訂單業績明細" }, // 核銷明細報表無獨立權限，沿用訂單業績明細
  "statistics/ledgerReport": { subKey: "訂單業績明細" },
  "statistics/personalProfit": { subKey: "個人分潤" },
  "statistics/performanceBonusTrial": { subKey: "業績獎金級距試算", checkView: true },
  // "statistics/daily": { subKey: "每日業績" },
  // "statistics/performance": { subKey: "分潤明細" },
  // "statistics/performancesetting": { subKey: "業積分潤設定" },

  // ===== 會員 =====
  "customer/list": { subKey: "會員列表" },
  "customer/intake-form": { subKey: "初診單管理", checkView: true }, // 查看 functionId 158
  "customer/document-batch-print": { subKey: "文件批次列印", checkView: true },
  "customer/signing-center": { subKey: "簽署中心", checkView: true },
  "customer/bulksending": { subKey: "發送訊息" },
  "customer/transfer": { subKey: "轉贈紀錄" },
  "customer/exchange": { subKey: "換購紀錄" },

  // ===== 預約（母路徑即子頁面）=====
  "appointment": { subKey: "預約行事曆" },

  // ===== 班表 =====
  "shiftschedule/employees": { subKey: "團隊成員" },
  // 後端僅提供「查看」functionId（如 125）；進頁需 經紀人.enable 且 查看.enable
  "shiftschedule/broker": { subKey: "經紀人", checkView: true },
  "shiftschedule/shifts": { subKey: "班別設定" },
  "shiftschedule/employeeshifts": { subKey: "班表管理" },
  "shiftschedule/service": { subKey: "服務上架" },
  "shiftschedule/categorymanagement": { subKey: "分類管理" },
  "shiftschedule/service-cost": { subKey: "成本與分潤" },
  "shiftschedule/consent-templates": { subKey: "範本設定", checkView: true },
  "shiftschedule/consent-editor": { subKey: "範本設定", checkView: true },
  "shiftschedule/consent-category-link": { subKey: "設定服務分類", checkView: true },
  "shiftschedule/consent-category-edit": { subKey: "設定服務分類", checkView: true },
  "shiftschedule/consent-service-link": { subKey: "服務關聯設定", checkView: true },
  "shiftschedule/consent-service-edit": { subKey: "服務關聯設定", checkView: true },
  "shiftschedule/payment-consent-template": { subKey: "付款同意書", checkView: true },
  "shiftschedule/payment-consent-category-link": { subKey: "付款同意書", checkView: true },
  "shiftschedule/payment-consent-category-edit": { subKey: "付款同意書", checkView: true },
  "shiftschedule/payment-consent-service-link": { subKey: "付款同意書", checkView: true },
  "shiftschedule/payment-consent-service-edit": { subKey: "付款同意書", checkView: true },
  "shiftschedule/post-treatment-consent-template": { subKey: "療程後確認單", checkView: true },
  "shiftschedule/questionnaire-templates": {
    subKey: QUESTIONNAIRE_PERMISSION_KEYS.templates,
    checkView: true,
  },
  "shiftschedule/questionnaire-editor": {
    subKey: QUESTIONNAIRE_PERMISSION_KEYS.templates,
    checkView: true,
  },
  "shiftschedule/questionnaire-category-link": {
    subKey: QUESTIONNAIRE_PERMISSION_KEYS.categories,
    checkView: true,
  },
  "shiftschedule/questionnaire-category-edit": {
    subKey: QUESTIONNAIRE_PERMISSION_KEYS.categories,
    checkView: true,
  },
  "shiftschedule/questionnaire-service-link": {
    subKey: QUESTIONNAIRE_PERMISSION_KEYS.services,
    checkView: true,
  },
  "shiftschedule/questionnaire-service-edit": {
    subKey: QUESTIONNAIRE_PERMISSION_KEYS.services,
    checkView: true,
  },

  // ===== 課程 =====
  "tickets/ticketManagement": { subKey: "課程管理" },
  // 後端尚未提供「卡項管理」子權限，暫時放行所有帳號；待權限鍵上線後移除 alwaysAllow
  "tickets/cardManagement": { subKey: "卡項管理", alwaysAllow: true },
};

/**
 * 檢查使用者是否有權限造訪指定 URL
 * 1. 先檢查母類別權限（如 銷售.enable）
 * 2. 再檢查子頁面權限（如 銷售.課程結帳.enable）
 * 3. 若 URL 不在子頁面對應表中，僅檢查母類別
 */
function isAllowed(url, permissions) {
  const parts = url.split("/").filter(Boolean);
  const urlParam = parts[0]; // e.g.,   "options", "sales", "accounting"
  const subPath = parts[1];  // e.g.,   "company", "ticket", "close"

  // 例外：公司資訊、喜好設定、修改密碼無論設定是否啟用都可造訪
  if (
    urlParam === "options" &&
    (subPath === "company" || subPath === "preferences" || subPath === "password")
  ) {
    return true;
  }

  if (!permissions || typeof permissions !== "object") {
    return false;
  }

  // 庫存（PIMS）有獨立的檢查邏輯
  if (urlParam === "pims") return checkPimsPermission(url, permissions);

  // 未開放功能：直連子路由一律擋下（避免僅母類別 enable 就放行）
  if (subPath && CLOSED_FEATURE_SUBPATHS.has(`${urlParam}/${subPath}`)) {
    return false;
  }

  const mapKey = subPath ? `${urlParam}/${subPath}` : urlParam;

  // 放行所有人的子頁（後端尚無對應權限鍵時使用），不受母類別權限限制
  if (SUB_PAGE_PERMISSIONS[mapKey]?.alwaysAllow) return true;

  // 取得母類別權限鍵名
  const categoryPermKey = CATEGORY_MAP[urlParam];
  if (!categoryPermKey) return false;

  // 檢查母類別權限
  if (!permissions[categoryPermKey]?.enable) return false;

  // 檢查子頁面權限
  const subPageConfig = SUB_PAGE_PERMISSIONS[mapKey];

  if (subPageConfig) {
    const catPerms = permissions[categoryPermKey];
    const subPagePasses = (subKey) => {
      const sp = catPerms?.[subKey];
      if (!sp || sp.enable !== true) return false;
      // methodKey：第三層非「查看」的權限（如 病歷管理／罐頭訊息）
      if (subPageConfig.methodKey) {
        return sp[subPageConfig.methodKey]?.enable === true;
      }
      if (subPageConfig.checkView) {
        return sp["查看"]?.enable === true;
      }
      return true;
    };
    if (subPagePasses(subPageConfig.subKey)) return true;
    if (subPageConfig.altSubKeys) {
      return subPageConfig.altSubKeys.some((altKey) => subPagePasses(altKey));
    }
    return false;
  }

  // 無子頁面映射（如 /sales、/accounting 母頁）→ 僅母類別權限
  return true;
}

function checkPimsPermission(url, permissions) {
  if (!permissions || typeof permissions !== 'object') {
    return false;
  }

  // 檢查是否有 "庫存" 分類且啟用
  if (!permissions["庫存"] || !permissions["庫存"].enable) {
    return false;
  }

  // 請採驗根路徑：任一子頁有「查看」權限即視為可進入（供 /pims 導向預設頁籤）
  if (url === "/pims" || url === "/pims/") {
    const entryPaths = [
      "/pims/vendors",
      "/pims/itemPrices",
      "/pims/inventoryList",
      "/pims/inventoryRecords",
      "/pims/stocktakes",
      "/pims/purchaseRequestsList",
    ];
    return entryPaths.some((entryPath) =>
      checkPimsPermission(entryPath, permissions)
    );
  }

  // 根據 URL 路徑判斷需要檢查哪個權限
  let permissionName = null;

  if (url.includes('/purchaseRequestsList') || url.includes('/purchase-requisitions')) {
    permissionName = "請購單";
  } else if (url.includes('/inventoryList') || url.includes('/inventories')) {
    permissionName = "庫存管理列表";
  } else if (url.includes('/itemPrices') || url.includes('/products')) {
    permissionName = "商品廠商價格";
  } else if (url.includes('/vendors') || url.includes('/suppliers')) {
    permissionName = "廠商管理";
  } else if (url.includes('/inventoryRecords') || url.includes('/inventory-transactions')) {
    permissionName = "出/入庫紀錄";
  } else if (url.includes('/stocktakes')) {
    permissionName = resolveStocktakePermissionKey(permissions);
    if (!permissionName) {
      return false;
    }
    const stocktakePerms = permissions["庫存"][permissionName];
    if (!stocktakePerms?.enable) {
      return false;
    }
    const isDifferenceReport =
      url.includes('/difference-report');
    const methodKey = isDifferenceReport ? "查看差異報告" : "查看";
    return stocktakePerms[methodKey]?.enable === true;
  } else {
    // 預設檢查庫存管理列表（向後兼容）
    permissionName = "庫存管理列表";
  }

  // 檢查對應的權限是否存在且啟用
  if (permissions["庫存"][permissionName] && permissions["庫存"][permissionName].enable) {
    // 檢查 "查看" 權限是否啟用
    return permissions["庫存"][permissionName]["查看"] &&
      permissions["庫存"][permissionName]["查看"].enable;
  }

  return false;
}

/** 後端 ch_name「盤點單」；舊資料可能仍為「盤點單列表」 */
function resolveStocktakePermissionKey(permissions) {
  const inventory = permissions?.["庫存"];
  if (!inventory?.enable) {
    return null;
  }
  if (inventory["盤點單"]) {
    return "盤點單";
  }
  if (inventory["盤點單列表"]) {
    return "盤點單列表";
  }
  return null;
}

/**
 * 檢查盤點單相關權限（functions id 162–172，ch_name「盤點單」）
 * @param {object} permissions - 使用者權限物件
 * @param {string} action -
 *   view | create | draft | delete | complete |
 *   viewDifferenceReport | draftDifferenceReport | submitDifferenceReport |
 *   rejectDifferenceReport | approveDifferenceReport | void
 */
function checkStocktakePermission(permissions, action) {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  if (!permissions["庫存"]?.enable) {
    return false;
  }

  const permissionKey = resolveStocktakePermissionKey(permissions);
  if (!permissionKey) {
    return false;
  }

  const stocktakePerms = permissions["庫存"][permissionKey];
  if (!stocktakePerms?.enable) {
    return false;
  }

  const actionMap = {
    view: "查看",
    create: "新增",
    draft: "暫存實盤資料",
    delete: "刪除",
    complete: "盤點完成",
    viewDifferenceReport: "查看差異報告",
    draftDifferenceReport: "校閱暫存",
    submitDifferenceReport: "送出審核",
    rejectDifferenceReport: "審核退回",
    approveDifferenceReport: "審核通過",
    void: "作廢審核通過",
  };

  const methodKey = actionMap[action];
  if (!methodKey) {
    return false;
  }

  return stocktakePerms[methodKey]?.enable === true;
}

/**
 * 檢查請購單相關權限
 * @param {object} permissions - 使用者權限物件
 * @param {string} action - 權限動作：'view' (查看), 'create' (新增), 'approve' (審核), 'firstReview' (初審), 'secondReview' (複審), 'ship' (出貨), 'receive' (入庫)
 * @returns {boolean} 是否有該權限
 */
function checkPurchaseRequisitionPermission(permissions, action) {
  if (!permissions || typeof permissions !== 'object') {
    return false;
  }

  // 檢查是否有 "庫存" 分類且啟用
  if (!permissions["庫存"] || !permissions["庫存"].enable) {
    return false;
  }

  // 檢查是否有 "請購單" 子權限且啟用
  const purchaseRequisitionPerms = permissions["庫存"]["請購單"];
  if (!purchaseRequisitionPerms || !purchaseRequisitionPerms.enable) {
    return false;
  }

  // 根據動作檢查對應的權限
  const actionMap = {
    'view': '查看',
    'create': '新增',
    'approve': '審核',
    'firstReview': '初審',
    'secondReview': '複審',
    'ship': '出貨',
    'receive': '入庫',
  };

  const permissionKey = actionMap[action];
  if (!permissionKey) {
    return false;
  }

  // 檢查對應的權限是否存在且啟用
  return purchaseRequisitionPerms[permissionKey] &&
    purchaseRequisitionPerms[permissionKey].enable === true;
}

/**
 * 會員列表頁內操作權限（後端 functions.ch_method 特規，與請購單 {@link checkPurchaseRequisitionPermission} 同型）。
 * @param {object} permissions - 使用者權限物件（Redux user.permissions）
 * @param {'export'} action - 目前僅支援 export（對應 ch_method「匯出會員」）
 * @returns {boolean}
 */
function checkMemberListPermission(permissions, action) {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  if (!permissions["會員"]?.enable) {
    return false;
  }
  const memberListPerms = permissions["會員"]["會員列表"];
  if (!memberListPerms || memberListPerms.enable !== true) {
    return false;
  }
  const actionMap = {
    export: "匯出會員",
  };
  const permissionKey = actionMap[action];
  if (!permissionKey) {
    return false;
  }
  return memberListPerms[permissionKey]?.enable === true;
}

/**
 * 結帳紀錄頁「匯出Excel」（後端 ch_method「匯出結帳紀錄」掛於 銷售／結帳紀錄）。
 */
function checkCheckoutRecordExportPermission(permissions) {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  if (!permissions["銷售"]?.enable) {
    return false;
  }
  const checkoutPerms = permissions["銷售"]["結帳紀錄"];
  if (!checkoutPerms || checkoutPerms.enable !== true) {
    return false;
  }
  return checkoutPerms["匯出結帳紀錄"]?.enable === true;
}

/**
 * 諮詢師業績頁「匯出Excel」（後端 ch_method「匯出業績」掛於 數據／諮詢師業績）。
 * 與路由權限一致：舊資料可能僅有「美容顧問業績」，兩鍵擇一檢查 {@link isAllowed} 之 altSubKeys。
 */
function checkConsultantPerformanceExportPermission(permissions) {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  if (!permissions["數據"]?.enable) {
    return false;
  }
  const dataPerms = permissions["數據"];
  const subKeys = ["諮詢師業績", "美容顧問業績"];
  const methodKey = "匯出業績";
  for (let i = 0; i < subKeys.length; i += 1) {
    const sp = dataPerms[subKeys[i]];
    if (sp?.enable === true && sp[methodKey]?.enable === true) {
      return true;
    }
  }
  return false;
}

/** 後端 ch_method：掛於 庫存／庫存管理列表（GET reports/effective-stock.xlsx 等匯出流程） */
function checkInventoryListExportPermission(permissions) {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  if (!permissions["庫存"]?.enable) {
    return false;
  }
  const listPerms = permissions["庫存"]["庫存管理列表"];
  if (!listPerms || listPerms.enable !== true) {
    return false;
  }
  return listPerms["匯出庫存"]?.enable === true;
}

/**
 * 後端 ch_method「匯出出/入庫紀錄及明細」掛於 庫存／出/入庫紀錄（GET reports/inventory-transactions.xlsx）。
 * 帳號權限管理 UI 另有 displayLabel「匯出紀錄 / 明細」，鍵名仍以後端為準。
 */
function checkInventoryTransactionsExportPermission(permissions) {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  if (!permissions["庫存"]?.enable) {
    return false;
  }
  const recPerms = permissions["庫存"]["出/入庫紀錄"];
  if (!recPerms || recPerms.enable !== true) {
    return false;
  }
  return recPerms["匯出出/入庫紀錄及明細"]?.enable === true;
}

/**
 * 商品廠商價格「成本」權限（functions id 160，ch_method「成本」）。
 * 有權限：可查看初始／系統平均／盤點成本，可編輯初始與盤點成本。
 */
function checkItemPricesCostPermission(permissions) {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  if (!permissions["庫存"]?.enable) {
    return false;
  }
  const itemPricesPerms = permissions["庫存"]["商品廠商價格"];
  if (!itemPricesPerms || itemPricesPerms.enable !== true) {
    return false;
  }
  return itemPricesPerms["成本"]?.enable === true;
}

/**
 * 檢查供應商分配相關權限
 * @param {object} permissions - 使用者權限物件
 * @param {string} action - 權限動作：'viewByProduct' (查看-以商品為主體), 'edit' (編輯), 'viewBySupplier' (查看-以供應商為主體)
 * @returns {boolean} 是否有該權限
 */
function checkSupplierAllocationPermission(permissions, action) {
  if (!permissions || typeof permissions !== 'object') {
    return false;
  }

  if (!permissions["庫存"] || !permissions["庫存"].enable) {
    return false;
  }

  const supplierAllocationPerms = permissions["庫存"]["供應商分配"];
  if (!supplierAllocationPerms || !supplierAllocationPerms.enable) {
    return false;
  }

  const actionMap = {
    'viewByProduct': '查看-以商品為主體',
    'edit': '編輯',
    'viewBySupplier': '查看-以供應商為主體',
  };

  const permissionKey = actionMap[action];
  if (!permissionKey) {
    return false;
  }

  return supplierAllocationPerms[permissionKey] &&
    supplierAllocationPerms[permissionKey].enable === true;
}

function hasPimsAccessForBranch(permissions, branchId) {
  if (!permissions || typeof permissions !== 'object') {
    return false;
  }

  // 檢查巢狀結構的庫存權限
  if (!permissions["庫存"] ||
    !permissions["庫存"].enable ||
    !permissions["庫存"]["庫存管理列表"] ||
    !permissions["庫存"]["庫存管理列表"].enable ||
    !permissions["庫存"]["庫存管理列表"]["查看"] ||
    !permissions["庫存"]["庫存管理列表"]["查看"].enable) {
    return false;
  }

  // 這裡需要看你的權限結構是否包含 branchIds
  // 如果權限中沒有 branchIds，可能需要用其他方式判斷
  return true; // 或者根據實際需求調整
}

function getAccessibleWarehouseIds(permissions) {
  if (!permissions || typeof permissions !== 'object') {
    return [];
  }

  // 檢查是否有庫存權限
  if (!permissions["庫存"] ||
    !permissions["庫存"].enable ||
    !permissions["庫存"]["庫存管理列表"] ||
    !permissions["庫存"]["庫存管理列表"].enable ||
    !permissions["庫存"]["庫存管理列表"]["查看"] ||
    !permissions["庫存"]["庫存管理列表"]["查看"].enable) {
    return [];
  }

  // 這裡也需要看你的權限結構是否包含 warehouseIds
  // 可能需要從其他地方取得，或者這個函數暫時不用
  return []; // 或者根據實際需求調整
}

/**
 * 根據使用者權限取得預設首頁路徑
 * 
 * 優先順序：
 * 1. 有權限的類別若有 defaultPath（如銷售、帳務），優先導向母頁面
 * 2. 否則尋找該類別第一個有權限的子頁面
 * 3. 若都沒有，導向 /options/company（公司資訊，例外放行）
 */
function getDefaultHomePage(permissions) {
  /**
   * 各類別的子頁面定義（依優先順序排列）
   * 後端回傳的權限若有對應頁面則啟用，無對應頁面或 sidebar 隱藏者註解保留。
   * - permissionKey: 權限物件中的分類鍵名（中文，與 API 回傳一致）
   * - defaultPath: 母頁面路徑（如有，優先使用，不需子頁面權限）
   * - subPages: 子頁面陣列，每項包含 subKey（API 子權限鍵名）與 path（路由）
   *   - checkView: true → 庫存(PIMS)需額外檢查「查看」權限
   *   - checkActions: [...] → 預約行事曆無「查看」，需至少一個操作權限 enable
   */
  const categoryRoutes = [
    {
      permissionKey: "設定",
      subPages: [
        { subKey: "公司資訊", path: "/options/company" },
        { subKey: "店家資訊", path: "/options/branch" },
        { subKey: "標籤管理", path: "/options/label" },
        { subKey: "初診單設定", path: "/options/intake-form", checkView: true },
        {
          subKey: MEDICAL_RECORD_PERMISSION_KEY,
          path: "/options/canned-message",
          methodKey: MEDICAL_RECORD_METHOD_KEYS.cannedMessage,
        },
        {
          subKey: MEDICAL_RECORD_PERMISSION_KEY,
          path: "/options/treatment-record-templates",
          methodKey: MEDICAL_RECORD_METHOD_KEYS.treatmentRecordTemplates,
        },
        { subKey: "自動化訊息設定", path: "/options/autoreply" },
        { subKey: "帳號權限管理", path: "/options/accountauthority" },
        { subKey: "POS付款/發票設定", path: "/options/pay", checkView: true },
        { subKey: "診間設定", path: "/options/consulting", checkView: true },
        // { subKey: "服務方式及繳款", path: "/options/serviceplan" },
        // { subKey: "設定付款方式", path: "/options/setPaymentMethod" },
        // { subKey: "金流串接", path: "/options/payment-gateway" },
        // { subKey: "品牌點數管理", path: "/options/point" },
        // { subKey: "儲值金管理", path: "/options/topup" },
        // { subKey: "訊息範本管理", path: "/options/messagetemplate" },
        // { subKey: "等級設定", path: "/options/level" },
        // { subKey: "黑名單管理", path: "/options/blacklist" }, // API 有，無對應路由
        // { subKey: "取消政策", path: "/options/cancellationpolicy" }, // API 有，無對應路由
        // { subKey: "訂金管理", path: "/options/deposit" }, // API 有，無對應路由
        // { subKey: "優惠券管理", path: "/options/coupon" }, // API 若有則可啟用
      ],
    },
    {
      permissionKey: "銷售",
      defaultPath: "/sales", // 銷售母頁面不需子頁面權限（/sales/index 亦同頁，仍可用）
      subPages: [
        { subKey: "課程結帳", path: "/sales/ticket" },
        // { subKey: "儲值金結帳", path: "/sales/topup" },
        { subKey: "結帳紀錄", path: "/sales/checkoutrecord", checkView: true },
        { subKey: "診所營運戰情室", path: "/sales/dashboard", checkView: true },
      ],
    },
    {
      permissionKey: "帳務",
      defaultPath: "/accounting", // 帳務母頁面不需子頁面權限
      subPages: [
        { subKey: "櫃檯人員日結", path: "/accounting/close", checkView: true },
        { subKey: "日結記錄", path: "/accounting/close-record", checkView: true },
        { subKey: "現金提領", path: "/accounting/cash-withdraw", checkView: true },
        { subKey: "帳務審核", path: "/accounting/account-audit", checkView: true },
        // { subKey: "欠款明細", path: "/accounting/outstanding-details" },
      ],
    },
    {
      permissionKey: "庫存",
      subPages: [
        { subKey: "廠商管理", path: "/pims/vendors", checkView: true },
        { subKey: "商品廠商價格", path: "/pims/itemPrices", checkView: true },
        { subKey: "庫存管理列表", path: "/pims/inventoryList", checkView: true },
        { subKey: "出/入庫紀錄", path: "/pims/inventoryRecords", checkView: true },
        { subKey: "盤點單", path: "/pims/stocktakes", checkView: true, altSubKeys: ["盤點單列表"] },
        { subKey: "請購單", path: "/pims/purchaseRequestsList", checkView: true },
      ],
    },
    {
      permissionKey: "數據",
      subPages: [
        { subKey: "諮詢師業績", path: "/statistics/consultantPerformance" },
        { subKey: "美容顧問業績", path: "/statistics/consultantPerformance" },
        { subKey: "業績報表", path: "/statistics/performanceReport" },
        { subKey: "業績報表", path: "/statistics/virtualPerformanceReport" }, // 虛擬業績報表沿用業績報表
        // { subKey: "核銷明細報表", path: "/statistics/verificationReport" }, // API 無此鍵
        { subKey: "訂單業績明細", path: "/statistics/orderPerformanceDetails" },
        { subKey: "訂單業績明細", path: "/statistics/verificationReport" }, // 核銷明細報表沿用訂單業績明細
        { subKey: "個人分潤", path: "/statistics/personalProfit" },
        { subKey: "業績獎金級距試算", path: "/statistics/performanceBonusTrial", checkView: true },
        // { subKey: "每日業績", path: "/statistics/daily" },
        // { subKey: "分潤明細", path: "/statistics/performance" },
        // { subKey: "業積分潤設定", path: "/statistics/performancesetting" }, // API 用「積」非「績」

      ],
    },
    {
      permissionKey: "會員",
      subPages: [
        { subKey: "會員列表", path: "/customer/list" },
        { subKey: "初診單管理", path: "/customer/intake-form", checkView: true },
        { subKey: "文件批次列印", path: "/customer/document-batch-print", checkView: true },
      { subKey: "簽署中心", path: "/customer/signing-center", checkView: true },
        { subKey: "簽署中心", path: "/customer/signing-center", checkView: true },
        { subKey: "發送訊息", path: "/customer/bulksending" },
        { subKey: "轉贈紀錄", path: "/customer/transfer" },
        { subKey: "換購紀錄", path: "/customer/exchange" },
      ],
    },
    {
      permissionKey: "預約",
      subPages: [
        { subKey: "預約行事曆", path: "/appointment", checkActions: ["新增預約", "編輯預約"] },
      ],
    },
    {
      permissionKey: "班表",
      subPages: [
        { subKey: "團隊成員", path: "/shiftschedule/employees" },
        { subKey: "經紀人", path: "/shiftschedule/broker", checkView: true },
        { subKey: "班別設定", path: "/shiftschedule/shifts" },
        { subKey: "班表管理", path: "/shiftschedule/employeeshifts" },
        { subKey: "服務上架", path: "/shiftschedule/service" },
        { subKey: "分類管理", path: "/shiftschedule/categorymanagement" },
        { subKey: "成本與分潤", path: "/shiftschedule/service-cost" },
        { subKey: "範本設定", path: "/shiftschedule/consent-templates", checkView: true },
        { subKey: "設定服務分類", path: "/shiftschedule/consent-category-link", checkView: true },
        { subKey: "服務關聯設定", path: "/shiftschedule/consent-service-link", checkView: true },
        { subKey: QUESTIONNAIRE_PERMISSION_KEYS.templates, path: "/shiftschedule/questionnaire-templates", checkView: true },
        { subKey: QUESTIONNAIRE_PERMISSION_KEYS.categories, path: "/shiftschedule/questionnaire-category-link", checkView: true },
        { subKey: QUESTIONNAIRE_PERMISSION_KEYS.services, path: "/shiftschedule/questionnaire-service-link", checkView: true },
        { subKey: "付款同意書", path: "/shiftschedule/payment-consent-template", checkView: true },
        { subKey: "療程後確認單", path: "/shiftschedule/post-treatment-consent-template", checkView: true },
      ],
    },
    {
      permissionKey: "課程",
      subPages: [
        { subKey: "課程管理", path: "/tickets/ticketManagement" },
        // 後端尚未提供「卡項管理」子權限，暫時放行所有帳號
        { subKey: "卡項管理", path: "/tickets/cardManagement", alwaysAllow: true },
      ],
    },
    // API 有「商品」類別、「商品列表」子權限，目前無對應路由，暫不加入
  ];

  for (const category of categoryRoutes) {
    const categoryPerms = permissions[category.permissionKey];

    // 該分類未啟用，跳過
    if (!categoryPerms?.enable) continue;

    // 無子頁權限問題，直接使用預設路徑（如銷售、帳務）
    if (category.defaultPath) {
      return category.defaultPath;
    }

    // 檢查子頁面權限，找到第一個有權限的子頁面
    if (category.subPages) {
      for (const subPage of category.subPages) {
        if (checkSubPagePermission(subPage, categoryPerms)) {
          return subPage.path;
        }
      }
    }
  }

  // 如果完全沒有任何可造訪的頁面，仍導向預設頁面
  return "/options/company";
}

/**
 * 各類別的子頁面定義（供 Sidebar 導航使用）
 * - permissionKey: 權限物件中的分類鍵名（中文，與 API 回傳一致）
 * - sidebarName: Sidebar 項目名稱（英文，與 SidebarType.js 一致）
 * - skipCategoryCheck: true → 不檢查母類別權限（如設定，因為公司資訊放行所有人）
 * - defaultPath: 母頁面路徑（如銷售、帳務，點擊後直接進入母頁面選單）
 * - subPages: 子頁面陣列
 *   - alwaysAllow: true → 放行所有人（如公司資訊）
 *   - checkView: true → 需額外檢查「查看」（庫存 PIMS、班表／經紀人）
 *   - checkActions: [...] → 預約需檢查操作權限
 */
const CATEGORY_ROUTES = [
  {
    permissionKey: "設定",
    sidebarName: "options",
    skipCategoryCheck: true, // 設定特殊：即使無設定權限，也能造訪公司資訊
    subPages: [
      { subKey: "公司資訊", path: "/options/company", alwaysAllow: true }, // 公司資訊放行所有人
      { subKey: "API串接", path: "/options/payment-gateway", checkView: true },
      { subKey: "喜好設定", path: "/options/preferences", alwaysAllow: true },
      { subKey: "修改密碼", path: "/options/password", alwaysAllow: true },
      { subKey: "店家資訊", path: "/options/branch" },
      { subKey: "標籤管理", path: "/options/label" },
      { subKey: "等級設定", path: "/options/member-management", checkView: true },
      { subKey: "贈品規則", path: "/options/purchase-gift", checkView: true },
      { subKey: "初診單設定", path: "/options/intake-form", checkView: true },
      {
        subKey: MEDICAL_RECORD_PERMISSION_KEY,
        path: "/options/canned-message",
        methodKey: MEDICAL_RECORD_METHOD_KEYS.cannedMessage,
      },
      {
        subKey: MEDICAL_RECORD_PERMISSION_KEY,
        path: "/options/treatment-record-templates",
        methodKey: MEDICAL_RECORD_METHOD_KEYS.treatmentRecordTemplates,
      },
      { subKey: "自動化訊息設定", path: "/options/autoreply" },
      { subKey: "帳號權限管理", path: "/options/accountauthority" },
      { subKey: "POS付款/發票設定", path: "/options/pay", checkView: true },
      { subKey: "診間設定", path: "/options/consulting", checkView: true },
    ],
  },
  {
    permissionKey: "銷售",
    sidebarName: "sales",
    defaultPath: "/sales", // 銷售母頁面不需子頁面權限（/sales/index 亦同頁，仍可用）
    subPages: [
      { subKey: "課程結帳", path: "/sales/ticket" },
      { subKey: "結帳紀錄", path: "/sales/checkoutrecord", checkView: true },
      { subKey: "診所營運戰情室", path: "/sales/dashboard", checkView: true },
    ],
  },
  {
    permissionKey: "帳務",
    sidebarName: "accounting",
    defaultPath: "/accounting", // 帳務母頁面不需子頁面權限
    subPages: [
      { subKey: "櫃檯人員日結", path: "/accounting/close", checkView: true },
      { subKey: "日結記錄", path: "/accounting/close-record", checkView: true },
      { subKey: "現金提領", path: "/accounting/cash-withdraw", checkView: true },
      { subKey: "帳務審核", path: "/accounting/account-audit", checkView: true },
    ],
  },
  {
    permissionKey: "庫存",
    sidebarName: "pims",
    subPages: [
      { subKey: "廠商管理", path: "/pims/vendors", checkView: true },
      { subKey: "商品廠商價格", path: "/pims/itemPrices", checkView: true },
      { subKey: "庫存管理列表", path: "/pims/inventoryList", checkView: true },
      { subKey: "出/入庫紀錄", path: "/pims/inventoryRecords", checkView: true },
      { subKey: "盤點單", path: "/pims/stocktakes", checkView: true, altSubKeys: ["盤點單列表"] },
      { subKey: "請購單", path: "/pims/purchaseRequestsList", checkView: true },
    ],
  },
  {
    permissionKey: "數據",
    sidebarName: "statistics",
    subPages: [
      { subKey: "諮詢師業績", path: "/statistics/consultantPerformance" },
      { subKey: "美容顧問業績", path: "/statistics/consultantPerformance" },
      { subKey: "業績報表", path: "/statistics/performanceReport" },
      { subKey: "訂單業績明細", path: "/statistics/orderPerformanceDetails" },
      { subKey: "訂單業績明細", path: "/statistics/verificationReport" }, // 核銷明細報表沿用訂單業績明細
      { subKey: "個人分潤", path: "/statistics/personalProfit" },
      { subKey: "業績獎金級距試算", path: "/statistics/performanceBonusTrial", checkView: true },
    ],
  },
  {
    permissionKey: "會員",
    sidebarName: "customer",
    subPages: [
      { subKey: "會員列表", path: "/customer/list" },
      { subKey: "初診單管理", path: "/customer/intake-form", checkView: true },
      { subKey: "文件批次列印", path: "/customer/document-batch-print", checkView: true },
      { subKey: "發送訊息", path: "/customer/bulksending" },
      { subKey: "轉贈紀錄", path: "/customer/transfer" },
      { subKey: "換購紀錄", path: "/customer/exchange" },
    ],
  },
  {
    permissionKey: "預約",
    sidebarName: "appointment",
    subPages: [
      { subKey: "預約行事曆", path: "/appointment", checkActions: ["新增預約", "編輯預約"] },
    ],
  },
  {
    permissionKey: "班表",
    sidebarName: "shiftschedule",
    subPages: [
      { subKey: "團隊成員", path: "/shiftschedule/employees" },
      { subKey: "經紀人", path: "/shiftschedule/broker", checkView: true },
      { subKey: "班別設定", path: "/shiftschedule/shifts" },
      { subKey: "班表管理", path: "/shiftschedule/employeeshifts" },
      { subKey: "服務上架", path: "/shiftschedule/service" },
      { subKey: "分類管理", path: "/shiftschedule/categorymanagement" },
      { subKey: "成本與分潤", path: "/shiftschedule/service-cost" },
      { subKey: "範本設定", path: "/shiftschedule/consent-templates", checkView: true },
      { subKey: "設定服務分類", path: "/shiftschedule/consent-category-link", checkView: true },
      { subKey: "服務關聯設定", path: "/shiftschedule/consent-service-link", checkView: true },
      { subKey: QUESTIONNAIRE_PERMISSION_KEYS.templates, path: "/shiftschedule/questionnaire-templates", checkView: true },
      { subKey: QUESTIONNAIRE_PERMISSION_KEYS.categories, path: "/shiftschedule/questionnaire-category-link", checkView: true },
      { subKey: QUESTIONNAIRE_PERMISSION_KEYS.services, path: "/shiftschedule/questionnaire-service-link", checkView: true },
      { subKey: "付款同意書", path: "/shiftschedule/payment-consent-template", checkView: true },
      { subKey: "療程後確認單", path: "/shiftschedule/post-treatment-consent-template", checkView: true },
    ],
  },
  {
    permissionKey: "課程",
    sidebarName: "tickets",
    subPages: [
      { subKey: "課程管理", path: "/tickets/ticketManagement" },
      // 後端尚未提供「卡項管理」子權限，暫時放行所有帳號
      { subKey: "卡項管理", path: "/tickets/cardManagement", alwaysAllow: true },
    ],
  },
];

/**
 * 依類別設定取得第一個可造訪路徑（不含使用者自訂首頁）
 */
function getFirstAvailablePathForCategory(category, permissions) {
  const categoryPerms = permissions?.[category.permissionKey];

  if (category.skipCategoryCheck) {
    for (const subPage of category.subPages) {
      if (subPage.alwaysAllow) {
        return subPage.path;
      }
    }
    if (categoryPerms?.enable) {
      for (const subPage of category.subPages) {
        if (
          !subPage.alwaysAllow &&
          checkSubPagePermission(subPage, categoryPerms)
        ) {
          return subPage.path;
        }
      }
    }
    return null;
  }

  if (!categoryPerms?.enable) {
    return null;
  }

  if (category.defaultPath) {
    return category.defaultPath;
  }

  if (category.subPages) {
    for (const subPage of category.subPages) {
      if (checkSubPagePermission(subPage, categoryPerms)) {
        return subPage.path;
      }
    }
  }

  return null;
}

/**
 * 解析模組預設首頁：使用者曾設定且仍有權限則沿用；否則第一個可進子頁。
 * 若曾設定但已無權限，會重寫 localStorage 為 fallback。
 */
function resolveModuleDefaultPathForNavigation(sidebarName, permissions) {
  const category = CATEGORY_ROUTES.find((c) => c.sidebarName === sidebarName);
  if (!category) {
    return { path: null, didReset: false };
  }

  const fallback = getFirstAvailablePathForCategory(category, permissions);
  if (!fallback) {
    return { path: null, didReset: false };
  }

  if (typeof window === "undefined") {
    return { path: fallback, didReset: false };
  }

  const savedPath = getSavedModuleDefaultPath(sidebarName);
  if (savedPath && isAllowed(savedPath, permissions)) {
    return { path: savedPath, didReset: false };
  }

  if (savedPath) {
    persistModuleDefaultPath(sidebarName, fallback);
    return { path: fallback, didReset: true };
  }

  return { path: fallback, didReset: false };
}

/**
 * 檢查子頁面是否有權限（內部共用函數）
 * @param {object} subPage - 子頁面設定
 * @param {object} categoryPerms - 母類別權限物件（可能為 undefined）
 */
function checkSubPagePermission(subPage, categoryPerms) {
  // 特殊放行（如公司資訊）
  if (subPage.alwaysAllow) return true;

  // 若母類別權限不存在，則無法檢查子頁面權限
  if (!categoryPerms) return false;

  let subPerms = categoryPerms[subPage.subKey];

  if (!subPerms && subPage.altSubKeys) {
    for (const altKey of subPage.altSubKeys) {
      if (categoryPerms[altKey]) {
        subPerms = categoryPerms[altKey];
        break;
      }
    }
  }

  if (subPage.methodKey) {
    // 設定／病歷管理：第三層為頁面名稱（如 罐頭訊息），非「查看」
    return subPerms?.enable === true && subPerms?.[subPage.methodKey]?.enable === true;
  } else if (subPage.checkView) {
    // 庫存（PIMS）、班表／經紀人：需「查看」啟用
    return subPerms?.enable && subPerms?.["查看"]?.enable;
  } else if (subPage.checkActions) {
    // 預約行事曆：無「查看」權限，需至少一個操作權限 enable
    if (subPerms?.enable) {
      return subPage.checkActions.some((action) => subPerms[action]?.enable);
    }
    return false;
  } else {
    // 一般分類：檢查子分類 enable 即可
    return subPerms?.enable;
  }
}

/**
 * 給 Sidebar 使用：檢查母類別權限並取得可造訪的路徑
 * 
 * 邏輯：
 * 1. 一般類別：檢查母類別權限 → 無權限則 allowed=false
 * 2. 設定類別：特殊處理，即使無設定權限也能造訪公司資訊
 * 3. 有母類別權限時，尋找可造訪的子頁面
 * 
 * @param {string} sidebarName - Sidebar 項目名稱（如 "customer", "sales"）
 * @param {object} permissions - 使用者權限物件
 * @returns {{ allowed: boolean, path: string | null }}
 *   - allowed: 是否允許導航（母類別有權限，或有特殊放行的子頁面）
 *   - path: 可造訪的路徑；若無權限，回傳 null
 */
function getSidebarNavigation(sidebarName, permissions) {
  if (!permissions || typeof permissions !== "object") {
    // 特例：即使沒有 permissions，設定的公司資訊也應該可以造訪
    if (sidebarName === "options") {
      return { allowed: true, path: "/options/company" };
    }
    return { allowed: false, path: null };
  }

  // 找到對應的類別設定
  const category = CATEGORY_ROUTES.find((c) => c.sidebarName === sidebarName);
  if (!category) {
    return { allowed: false, path: null };
  }

  const categoryPerms = permissions[category.permissionKey];
  const hasCategoryPermission = categoryPerms?.enable;

  // 特殊處理：設定類別即使無母類別權限，也要檢查是否有 alwaysAllow 的子頁面
  if (category.skipCategoryCheck) {
    const { path } = resolveModuleDefaultPathForNavigation(
      sidebarName,
      permissions
    );
    if (path) {
      return { allowed: true, path };
    }
    return { allowed: false, path: null };
  }

  // 一般類別：檢查母類別權限
  if (!hasCategoryPermission) {
    return { allowed: false, path: null };
  }

  // 母類別有權限，尋找可造訪的子頁面
  // 若有 defaultPath（如銷售、帳務的母頁面），直接使用
  if (category.defaultPath) {
    return { allowed: true, path: category.defaultPath };
  }

  // 請採驗：沿用原有 localStorage 預設頁籤（與 Pims index 導向一致）
  if (category.sidebarName === "pims") {
    const path = getPimsDefaultPath(permissions);
    if (path && checkPimsPermission(path, permissions)) {
      return { allowed: true, path };
    }
  }

  const { path } = resolveModuleDefaultPathForNavigation(
    sidebarName,
    permissions
  );
  if (path) {
    return { allowed: true, path };
  }

  // 母類別有權限但沒有任何可造訪的子頁面（理論上不應發生，因為母類別 enable 代表至少有一個子頁面）
  return { allowed: false, path: null };
}

/**
 * 「本功能未開放」時的導向：與 Sidebar 相同邏輯，導向該模組第一個可造訪路徑
 * （如班表無母層 index，會到 /shiftschedule/employees 等子頁，而非空白 /shiftschedule）。
 * 若該模組完全無權限，則改為 {@link getDefaultHomePage}。
 */
function getClosedFeatureRedirectPath(url, permissions) {
  if (!url || typeof url !== "string") {
    return getDefaultHomePage(permissions);
  }
  const parts = url.split("/").filter(Boolean);
  if (parts.length < 2) {
    return getDefaultHomePage(permissions);
  }
  const sidebarName = parts[0];
  return getModuleHomePage(sidebarName, permissions);
}

/**
 * 依目前所在模組（Sidebar 名稱）導向該模組首頁，與點擊側欄圖示／進入模組邏輯一致。
 * 無法解析模組或無權限時，改為 {@link getDefaultHomePage}。
 */
function getModuleHomePage(sidebarName, permissions) {
  if (!sidebarName || typeof sidebarName !== "string") {
    return getDefaultHomePage(permissions);
  }
  const { allowed, path } = getSidebarNavigation(sidebarName, permissions);
  if (allowed && path) {
    return path;
  }
  return getDefaultHomePage(permissions);
}

/**
 * 檢查指定子頁面的單一 method 權限（如 新增／編輯／刪除／查看）。
 */
function checkSubPageMethodPermission(permissions, category, subKey, method) {
  if (!permissions || typeof permissions !== "object") {
    return false;
  }
  const categoryPerms = permissions[category];
  if (!categoryPerms?.enable) {
    return false;
  }
  const subPerms = categoryPerms[subKey];
  if (!subPerms?.enable) {
    return false;
  }
  return subPerms[method]?.enable === true;
}

export {
  isAllowed,
  isClosedFeatureRoute,
  getClosedFeatureRedirectPath,
  checkPimsPermission,
  checkPurchaseRequisitionPermission,
  checkStocktakePermission,
  resolveStocktakePermissionKey,
  checkSubPageMethodPermission,
  checkMemberListPermission,
  checkCheckoutRecordExportPermission,
  checkConsultantPerformanceExportPermission,
  checkInventoryListExportPermission,
  checkInventoryTransactionsExportPermission,
  checkItemPricesCostPermission,
  checkSupplierAllocationPermission,
  hasPimsAccessForBranch,
  getAccessibleWarehouseIds,
  getDefaultHomePage,
  getModuleHomePage,
  getSidebarNavigation,
  resolveShiftSchedulePermissionSubKey,
  resolveShiftScheduleMenuPagination,
  resolveOptionsMenuPermission,
};
